import { IntlShape } from 'react-intl'

import { addMinutes, isFuture, isPast, subDays } from 'date-fns'
import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { isEmpty } from 'lodash-es'
import moment from 'moment-timezone'

import { INITIAL_DAYS_OF_AVAILABILITY_SHOWN } from './constants'
import { getDateFromAppointment } from './getDateFromAppointment'
import { Appointment, GuardianInfo } from '../../models/appointment/Appointment'
import { Assignment } from '../../models/assignments/Assignments'
import { AvailabilityDate, InPersonLocation, ModalityType } from '../../models/calendar/CalendarV2'
import {
  Episode,
  EpisodeParticipant,
  EpisodeParticipantRole,
  EpisodeStates,
  RecipientType,
} from '../../models/episodes/Episodes'
import { Note, NoteStatusStates } from '../../models/notes/Notes'
import { ProgramNames } from '../../models/programs/Programs'
import {
  Address,
  daSessionSupportedProviderTypes,
  Provider,
  ProviderAvailabilityBreakdown,
  ProviderCalendar,
  ProviderCalendarEntry,
  ProviderInfo,
  SESSION_TYPES,
  sessionSupportedProviderTypes,
} from '../../models/providers/Providers'
import { APPOINTMENT_STATUS, MEETING_FORMATS } from '../../models/session/Session'
import { S4C_PROPERTIES } from '../common/constants/mixpanelConstants'
import {
  TREATMENT_OPTIONS_FROM_FRONT_END,
  TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND,
} from '../common/constants/treatmentOptions'
import { getISO8601DateWithTimezone } from '../common/utils/dateUtils'
import { isAgeTeen } from '../common/utils/userUtils'
import { getProgramNameFromId, isDAEngageProgram } from '../programConfigs/programHelpers'
import { DBT_TEENS_SESSION_TYPE_LABELS, isCaregiverSessionType, isGroupSessionType } from '../sessions/constants'

/**
 * Checks a list of providers and gets all the available supported appointments for the user.
 *
 * @param providers - Array of providers with appointments
 * @returns Array of supported appointments (LCx and DA Engage only)
 */
export const getSupportedAppointments = (providers: Provider[]): Appointment[] => {
  const appts = providers
    .map((provider: Provider) => provider.appointments)
    .flat()
    .filter((appt: Appointment) => appointmentIsSupported(appt))
  return appts
}

/**
 * @deprecated use `getDateFromAppointment`
 */
export const getDateTimeObject = ({
  startDate = '',
  startTime = '',
  timeZone = '',
}: {
  startDate?: string
  startTime?: string
  timeZone?: string
}): moment.Moment => {
  return moment.tz(`${startDate} ${startTime}`, timeZone)
}

/**
 * @deprecated use `getDateFromAppointment`
 */
export const getAppointmentDateTimeObject = ({ startDate, startTime, timeZone }: Appointment): moment.Moment => {
  return getDateTimeObject({ startDate, startTime, timeZone })
}

/**
 * Check if the appointment meetingFormat is supported, appointment status is present + future,
 * and provider supports this session type.
 *
 * DA provider types are only supported when the appointment belongs to a DA Engage program
 * (determined via programId). Legacy DA appointments (no programId) are excluded.
 *
 * @param appt - The appointment to check
 * @param isDAEngageOnOffPlatformVideoEnabled - Optional flag used by provider portal to
 *   unconditionally include DA provider types. Client apps should omit this and let the
 *   function determine DA Engage support from the appointment's programId.
 * @returns true if the appointment is supported
 */
export const appointmentIsSupported = (appt: Appointment, isDAEngageOnOffPlatformVideoEnabled = false): boolean => {
  const includeDAProviders =
    isDAEngageOnOffPlatformVideoEnabled || isDAEngageProgram(getProgramNameFromId(appt.programId))
  const supportedTypes = includeDAProviders
    ? [...sessionSupportedProviderTypes, ...daSessionSupportedProviderTypes]
    : sessionSupportedProviderTypes

  return (
    ['live_messaging', 'video'].includes(appt.meetingFormat) &&
    ['newAppt', 'rescheduled'].includes(appt.appointmentStatus) &&
    (appt.provider == null ||
      supportedTypes.some((providerType) => providerType.lyraType === appt.provider?.lyra_type.toLowerCase()))
  )
}

// 'now' or: 59:99
export const calculateTimeLeft = ({ startTime }: { startTime: moment.Moment }) => {
  const duration = moment.duration(startTime.diff(moment()))
  const startsInFormatted =
    duration.asMilliseconds() <= 0 ? 'now' : moment.utc(duration.asMilliseconds()).format('mm:ss')
  return startsInFormatted
}

// get the text that shows in the 'Join' button
export const getJoinButtonText = (sessionType: SESSION_TYPES, startsIn: string, isMobile: boolean) => {
  let joinText = ''
  if (isMobile) return 'Join'
  if (sessionType === SESSION_TYPES.MESSAGE) {
    joinText = startsIn === 'now' ? 'Join (In progress)' : startsIn === '' ? 'Join' : `Join (Starts in ${startsIn})`
  } else {
    // is a video session
    joinText = startsIn === 'now' ? 'Start Session' : startsIn === '' ? 'Join' : `Join (Starts in ${startsIn})`
  }
  return joinText
}

/**
 * Helper to get the current appointment
 * @param {*} providerId string
 * @param {*} episodeId string
 * @param {*} appointments Providers
 * @param {*} sessionCount number
 * @returns appointment | null
 */
export const getAppointmentFromSessionNumber = (
  providerId: string,
  episodeId: string | null,
  appointments: Appointment[] | undefined,
  sessionCount: number,
): Appointment | null => {
  const providerEpisodeAppointments = appointments?.filter(
    (appointment) => appointment.lyraProviderId === providerId && appointment.episodeId === episodeId,
  )
  const appointment = providerEpisodeAppointments?.find((appointment) => appointment.sessionNumber === sessionCount)
  return appointment || null
}

export const isExpeditedBookingAppointment = (appointment: Appointment) => {
  if (appointment && appointment?.createDate && appointment?.startDate && appointment?.startTime) {
    // createDate comes in like this: 2023-04-13T16:18:26.000+00:00
    const cleanedCreateDate = appointment.createDate.replace(/\..*/, '')
    const createDateTimeParts = cleanedCreateDate.split('T')
    const createDateTime = getDateTimeObject({
      startDate: createDateTimeParts[0],
      startTime: createDateTimeParts[1],
      timeZone: 'UTC',
    })
    const startDateTime = getDateTimeObject({
      startDate: appointment.startDate,
      startTime: appointment.startTime,
      timeZone: appointment.timeZone,
    })
    const dayInMilliseconds = 24 * 60 * 60 * 1000
    return startDateTime.valueOf() - createDateTime.valueOf() < dayInMilliseconds
  }
  return false
}

export enum AppointmentClass {
  INITIAL = 'initial',
  RECURRING = 'recurring',
}

export const sortAppointments = (appointments: Appointment[]) =>
  appointments?.sort(
    (a, b) =>
      getDate({ date: a.startDate, time: a.startTime, timeZone: a.timeZone }).getTime() -
      getDate({ date: b.startDate, time: b.startTime, timeZone: b.timeZone }).getTime(),
  )

export const getAppointmentClass = (appointments: Appointment[]): AppointmentClass =>
  appointments.some((appointment) => appointment.appointmentStatus === 'completed')
    ? AppointmentClass.RECURRING
    : AppointmentClass.INITIAL

export const getDate = ({ date, time, timeZone }: { date: string; time: string; timeZone: string }) =>
  zonedTimeToUtc(`${date}T${time}`, timeZone)

export const getSessionNumber = (dateTime: string, appointments: Appointment[]) => {
  const sortedAppointments = sortAppointments(appointments)

  let sessionNumber = 1
  for (const appointment of sortedAppointments) {
    const appointmentDate = getDate({
      date: appointment.startDate,
      time: appointment.startTime,
      timeZone: appointment.timeZone,
    })
    if (new Date(dateTime).getTime() < appointmentDate.getTime()) {
      return sessionNumber
    }

    sessionNumber++
  }

  return sessionNumber
}

export const getCurrentAppointment = (appointments: Appointment[]): Appointment | undefined => {
  const sortedAppointments = sortAppointments(appointments)

  return sortedAppointments?.find((appointment) => {
    const sessionStart = getDateFromAppointment(appointment)
    return isFuture(sessionStart) && appointment.appointmentStatus !== APPOINTMENT_STATUS.CANCELED
  })
}

export const getLastAppointment = (appointments: Appointment[]): Appointment | undefined => {
  const sortedAppointments = sortAppointments(appointments)

  return [...(sortedAppointments ?? [])].reverse().find((appointment) => {
    const sessionStart = getDateFromAppointment(appointment)
    return isPast(sessionStart) && appointment.appointmentStatus !== APPOINTMENT_STATUS.CANCELED
  })
}

export const getFollowingAppointment = (
  currentAppointmentId: number,
  appointments: Appointment[],
): Appointment | undefined => {
  const sortedAppointments = sortAppointments(
    appointments.filter((appointment) => appointment.appointmentStatus !== APPOINTMENT_STATUS.CANCELED),
  )

  const currentAppointmentIndex = sortedAppointments.findIndex(
    (appointment) => appointment.appointmentId === currentAppointmentId,
  )

  if (currentAppointmentIndex === -1) {
    return undefined
  }

  if (currentAppointmentIndex >= sortedAppointments.length - 1) {
    return undefined
  }

  return sortedAppointments[currentAppointmentIndex + 1]
}

export const getDisplayAddress = ({
  address,
  isOnsiteProvider,
  provider,
}: {
  address?: Address | undefined
  isOnsiteProvider?: boolean
  provider?: ProviderInfo | undefined
}) => {
  const appointmentAddress = isOnsiteProvider && !isEmpty(address) ? address : provider?.nearestAddress
  const addressLine1 = `${appointmentAddress?.street1?.trim()}`.concat(
    appointmentAddress?.street2?.trim() ? `, ${appointmentAddress?.street2?.trim()}` : '',
  )
  const addressLine2 = `${appointmentAddress?.city}, ${appointmentAddress?.state} ${
    appointmentAddress?.zipcode ?? appointmentAddress?.zip
  }`

  return addressLine1 + ', ' + addressLine2
}

export const isFollowUpBCMorCLEAppointment = (appointment: Appointment) => {
  return (
    appointment.sessionNumber > 1 &&
    [
      TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.BLENDED_CARE_MEDS as string,
      TREATMENT_OPTIONS_FROM_FRONT_END.CLINICAL_LEAVE_EVALUATION as string,
    ].includes(appointment.treatmentType)
  )
}

// Legacy (non-Engage) DA appointments predate the program model and have no programId; BCx and DA Engage both carry one.
export const isLegacyDirectAccessAppointment = (appointment: Appointment) => isEmpty(appointment.programId)

/**
 * Helper function to check if the appointment is of type meeting format
 * @param appt Appointment
 * @param meetingFormat MEETING_FORMATS
 */
export const isApptMeetingFormat = (appt: Appointment | null, meetingFormat: MEETING_FORMATS): boolean => {
  return appt ? appt.meetingFormat === meetingFormat : false
}

export const getAppointmentRange = (
  startDate?: string,
  startTime?: string,
  timeZone?: string,
  duration?: number,
): { fromDate: Date; toDate: Date } => {
  const appointmentStart = getDateTimeObject({
    startDate,
    startTime,
    timeZone,
  })
  const appointmentInterval: number = duration ?? 60
  const appointmentEnd = appointmentStart.clone().add(appointmentInterval, 'minutes')
  return { fromDate: new Date(appointmentStart.format()), toDate: new Date(appointmentEnd.format()) }
}

export const getDisplayedTimeslots = ({
  dates,
  daysOfAvailability,
}: {
  dates: Array<AvailabilityDate>
  daysOfAvailability: number
}) => {
  return dates.slice(0, daysOfAvailability).map((dateData) => {
    const { date, times } = dateData
    return {
      date,
      times: times
        .filter((dateTime) => Boolean(dateTime))
        .map((dateTime) => ({
          datetime: dateTime.datetime,
          modality_type: dateTime.modality_type,
          location: dateTime.location,
        })),
    }
  })
}

export interface CreateUpdatedScheduleTrackingDataParams {
  // In UTC
  dates?: Array<AvailabilityDate>
  timeZone: string
  daysOfAvailabilityShown?: number
  matchingScheduleSlotsString?: string[] | undefined
  shouldShowAvailabilityPreference?: boolean
  isProviderOnIPC?: boolean
}

export const createUpdatedScheduleTrackingData = ({
  dates,
  timeZone,
  daysOfAvailabilityShown = INITIAL_DAYS_OF_AVAILABILITY_SHOWN,
  matchingScheduleSlotsString,
  shouldShowAvailabilityPreference,
  isProviderOnIPC = false,
}: CreateUpdatedScheduleTrackingDataParams) => {
  if (!dates) {
    return {}
  }
  const calendarSlotsShown = getDisplayedTimeslots({
    dates,
    daysOfAvailability: daysOfAvailabilityShown,
  })
    .flatMap((timeslots) => timeslots.times)
    .filter((dateTime) => dateTime?.datetime) // Filter out null datetimes first
    .map((dateTime) => {
      return {
        time: getISO8601DateWithTimezone({
          date: zonedTimeToUtc(dateTime.datetime!, timeZone), // Use ! since we filtered nulls
          timeZone,
        }),
        modality_type: dateTime.modality_type,
        location: dateTime.location,
      }
    })

  const matchingScheduleSlotsInClientTimezone = matchingScheduleSlotsString?.map((scheduleSlot) => {
    return getISO8601DateWithTimezone({
      date: utcToZonedTime(scheduleSlot, timeZone),
      timeZone,
    })
  })

  return {
    [S4C_PROPERTIES.CALENDAR_SLOTS_SHOWN]: calendarSlotsShown,
    [S4C_PROPERTIES.NUM_CALENDAR_SLOTS]: calendarSlotsShown?.length,
    ...(shouldShowAvailabilityPreference && {
      [S4C_PROPERTIES.MATCHED_CALENDAR_SLOTS_SHOWN]: matchingScheduleSlotsInClientTimezone,
    }),
    [S4C_PROPERTIES.IPC_FLAG]: isProviderOnIPC,
  }
}

export const getAppointmentTimeslot = ({
  appointment,
  intl,
  showEndTime = true,
  lowercaseDayPeriod = true,
  dateTimeSeparator = ', ',
}: {
  appointment: Appointment
  intl: IntlShape
  showEndTime?: boolean
  lowercaseDayPeriod?: boolean
  dateTimeSeparator?: string
}) => {
  const formatTime = ({
    date,
    showTimezone = true,
    showDayPeriod = true,
  }: {
    date: Date
    showTimezone?: boolean
    showDayPeriod?: boolean
  }) => {
    return intl
      .formatTime(date, {
        hour: 'numeric',
        minute: 'numeric',
        ...(showTimezone && { timeZoneName: 'short' }),
      })
      .replace(/ ?(AM|PM)/, (m) => {
        if (!showDayPeriod) return ''
        return lowercaseDayPeriod ? m.toLowerCase() : m
      })
  }
  const appointmentStart = getDateFromAppointment(appointment)
  const appointmentEnd = addMinutes(appointmentStart, appointment.appointmentDuration)
  const isFlankingMeridiem = format(appointmentStart, 'a') !== format(appointmentEnd, 'a')
  const isThisYear = appointmentStart.getFullYear() === new Date().getFullYear()
  const formattedAppointmentDate = intl.formatDate(appointmentStart, {
    day: 'numeric',
    weekday: 'short',
    month: 'short',
    ...(!isThisYear && { year: 'numeric' }),
  })
  const formattedAppointmentTime = showEndTime
    ? `${formatTime({
        date: appointmentStart,
        showDayPeriod: isFlankingMeridiem,
        showTimezone: false,
      })} - ${formatTime({
        date: appointmentEnd,
      })}`
    : formatTime({ date: appointmentStart })

  return `${formattedAppointmentDate}${dateTimeSeparator}${formattedAppointmentTime}`
}

export const isNoteSubmittedForAppointment = (
  notes: Note[] | undefined,
  appointmentId: number | undefined,
): boolean | undefined => {
  if (!appointmentId) {
    return undefined
  }
  return notes?.some((note) => note.appointment_id === appointmentId && note.status === NoteStatusStates.COMPLETED)
}

/**
 * Extracts guardian information from a list of appointments for an episode,
 * optionally supplemented by episode participant data.
 * Episode participant data (if provided) takes priority for fields it contains
 * (guardianLyraId, guardianEmail, guardianPhone), falling back to appointment data.
 * Callers may opt into treating an active SECONDARY_PATIENT as the guardian for DBT Teens,
 * where the caregiver is modeled as a second patient on the teen's episode.
 * Fields only available on appointments (firstName, lastName, phoneCountryCode) are
 * always sourced from appointments.
 * Only includes fields that have truthy values to avoid overwriting existing data when spread.
 */
export const getGuardianInfoForEpisode = (
  appointments: Appointment[],
  episodeParticipants?: EpisodeParticipant[],
  options: { includeSecondaryPatientAsGuardian?: boolean } = {},
): GuardianInfo => {
  const primaryGuardian = episodeParticipants?.find((p) => p.role === EpisodeParticipantRole.PRIMARY_GUARDIAN)
  const secondaryPatientGuardian = options.includeSecondaryPatientAsGuardian
    ? episodeParticipants?.find(
        (p) => p.role === EpisodeParticipantRole.SECONDARY_PATIENT && p.status?.toLowerCase() === 'active',
      )
    : undefined
  const guardianParticipant = secondaryPatientGuardian ?? primaryGuardian

  const guardianLyraId =
    guardianParticipant?.participant_lyra_id || appointments.find((a) => a.guardianLyraId)?.guardianLyraId
  const guardianEmail =
    guardianParticipant?.email_notification_address || appointments.find((a) => a.guardianEmail)?.guardianEmail
  const guardianFirstName = appointments.find((a) => a.guardianFirstName)?.guardianFirstName
  const guardianLastName = appointments.find((a) => a.guardianLastName)?.guardianLastName
  const guardianPhone =
    guardianParticipant?.sms_notification_number || appointments.find((a) => a.guardianPhone)?.guardianPhone
  const guardianPhoneCountryCode = appointments.find((a) => a.guardianPhoneCountryCode)?.guardianPhoneCountryCode
  const guardianVoicemailConsent = guardianParticipant?.voicemail_consent ?? undefined

  return {
    ...(guardianLyraId && { guardianLyraId }),
    ...(guardianEmail && { guardianEmail }),
    ...(guardianFirstName && { guardianFirstName }),
    ...(guardianLastName && { guardianLastName }),
    ...(guardianPhone && { guardianPhone }),
    ...(guardianPhoneCountryCode && { guardianPhoneCountryCode }),
    ...(guardianVoicemailConsent != null && { guardianVoicemailConsent }),
  }
}

/**
 * Determines whether the currently logged-in user is the teen (the episode's patient) or the
 * caregiver for a DBT for Teens episode. Used to drive role-specific behavior (session-list
 * rendering, manage-action gating) — see LCDELIVERY-7302.
 *
 * DBT for Teens models the parent as a SECONDARY_PATIENT participant on the teen's episode
 * ("Option E", LCDELIVERY-7280 spike; enum value from LCDELIVERY-7498, row provisioning from
 * LCDELIVERY-7499). PRIMARY_GUARDIAN / appointment guardian fields are kept as fallbacks for
 * data written before participant provisioning and for guardian-attributed bookings.
 *
 * Resolution order, most authoritative first:
 *  1. Active SECONDARY_PATIENT participant match                 → RecipientType.CAREGIVER
 *     (checked before the patient match so a caregiver session that carries the PARENT as its
 *     patient still classifies the parent correctly — the parent is also a patient on the episode)
 *  2. Identity match against the episode's patient_id            → RecipientType.TEEN
 *     (callers should pass the EPISODE's patient_id when available, not the appointment's userId,
 *     since the episode's primary patient is authoritatively the teen)
 *  3. Resolved guardian match (participants/appointment fields)  → RecipientType.CAREGIVER
 *  4. Best-effort age fallback (only when episode context is unavailable / no identity match):
 *     a 13–17 year-old is treated as the teen, otherwise the caregiver.
 *
 * Returns undefined when the role cannot be determined (no current user id and no age signal).
 */
export const getDbtTeensParticipantRole = ({
  currentUserLyraId,
  patientId,
  episodeParticipants,
  appointments = [],
  currentUserAge,
}: {
  currentUserLyraId?: string | null
  patientId?: string | null
  episodeParticipants?: EpisodeParticipant[]
  appointments?: Appointment[]
  currentUserAge?: number | null
}): RecipientType | undefined => {
  if (currentUserLyraId != null) {
    const secondaryPatient = episodeParticipants?.find(
      (participant) =>
        participant.role === EpisodeParticipantRole.SECONDARY_PATIENT &&
        participant.participant_lyra_id === currentUserLyraId,
    )
    if (secondaryPatient?.status === 'active') {
      return RecipientType.CAREGIVER
    }
    if (patientId != null && currentUserLyraId === patientId) {
      return RecipientType.TEEN
    }
    // An inactive SECONDARY_PATIENT record is authoritative. Do not let legacy guardian or age
    // fallbacks restore caregiver access after that participant relationship has ended.
    if (secondaryPatient != null) {
      return undefined
    }
    const { guardianLyraId } = getGuardianInfoForEpisode(appointments, episodeParticipants)
    if (guardianLyraId != null && currentUserLyraId === guardianLyraId) {
      return RecipientType.CAREGIVER
    }
  }
  // Fallback: no identity match (e.g. episode/guardian data not yet loaded). Use age as a
  // best-effort signal. Documented as imprecise — it does not survive the 17→18 transition.
  if (currentUserAge != null) {
    return isAgeTeen(currentUserAge) ? RecipientType.TEEN : RecipientType.CAREGIVER
  }
  return undefined
}

/**
 * Whether the current member may join an appointment from their own account.
 *
 * DBT for Teens only allows the episode patient (the teen) to use the in-product joining flow for
 * the MVP. The caregiver is represented by an active SECONDARY_PATIENT episode participant, while
 * `guardianLyraId` is only legacy/initial-booking metadata and is not guaranteed on later sessions.
 * Unknown DBT identities fail closed because showing the banner exposes a join action the backend
 * cannot authorize for a caregiver.
 *
 * Other programs retain the existing appointment-guardian behavior.
 */
export const canMemberJoinSession = ({
  appointment,
  currentUserLyraId,
  episode,
}: {
  appointment?: Appointment | null
  currentUserLyraId?: string | null
  episode?: Episode
}): boolean => {
  if (appointment == null) {
    return false
  }

  const isAppointmentGuardian = Boolean(appointment.guardianLyraId) && appointment.guardianLyraId === currentUserLyraId
  const programName = episode?.program_name ?? getProgramNameFromId(appointment.programId)
  const hasDbtTeensSessionType =
    appointment.sessionType != null &&
    Object.prototype.hasOwnProperty.call(DBT_TEENS_SESSION_TYPE_LABELS, appointment.sessionType)
  const isDbtTeensAppointment =
    programName === ProgramNames.DialecticalBehaviorTherapyTeens || (programName == null && hasDbtTeensSessionType)

  if (!isDbtTeensAppointment) {
    return !isAppointmentGuardian
  }

  const role = getDbtTeensParticipantRole({
    currentUserLyraId,
    patientId:
      episode?.patient_id ??
      appointment.childLyraId ??
      // For an existing episode, userId may be the caregiver on a Parenting session. Wait for the
      // authoritative episode instead of treating that appointment owner as the teen.
      (appointment.episodeId ? undefined : appointment.userId),
    episodeParticipants: episode?.participants,
    appointments: [appointment],
  })

  return role === RecipientType.TEEN
}

/**
 * Whether the current member may manage (reschedule / cancel) a given session, per the
 * DBT for Teens session-management rules — LCDELIVERY-7302 §4.2 + Q-FE-4.
 *
 *  - LCT Teens (ProgramNames.TeensTherapy): a guardian who booked for their teen cannot manage the
 *    teen's session (unchanged existing behavior).
 *  - DBT for Teens: the caregiver (SECONDARY_PATIENT) may manage ALL appointment types; the teen may
 *    manage all types EXCEPT caregiver ("Parenting") sessions. Enforcement is FE-only for MVP (Q-FE-4).
 *  - All other programs: unrestricted.
 *
 * Positive framing (true = allowed) so callers can drive "show reschedule/cancel controls" directly;
 * SessionList negates it for its `disallowSessionUpdate` prop.
 */
export const canMemberManageSession = ({
  programName,
  sessionType,
  currentUserLyraId,
  patientId,
  guardianLyraId,
  episodeParticipants,
  appointments = [],
}: {
  programName?: ProgramNames | string | null
  sessionType?: string | null
  currentUserLyraId?: string | null
  patientId?: string | null
  guardianLyraId?: string | null
  episodeParticipants?: EpisodeParticipant[]
  appointments?: Appointment[]
}): boolean => {
  if (programName === ProgramNames.TeensTherapy) {
    // The appointment is booked for a teen by their guardian → the guardian cannot manage it.
    const isParentLedLctTeenBooking =
      guardianLyraId != null && patientId !== guardianLyraId && currentUserLyraId === guardianLyraId
    return !isParentLedLctTeenBooking
  }
  if (programName === ProgramNames.DialecticalBehaviorTherapyTeens) {
    // Provider-facing SessionList callers do not have member identity context. Member-specific
    // restrictions only apply when there is a logged-in member whose role can be resolved.
    if (currentUserLyraId == null) {
      return true
    }
    const role = getDbtTeensParticipantRole({ currentUserLyraId, patientId, episodeParticipants, appointments })
    // An active caregiver may manage every DBT Teens appointment. A teen may manage every type
    // except the caregiver ("Parenting") session. Unknown and inactive participants get no controls.
    return role === RecipientType.CAREGIVER || (role === RecipientType.TEEN && !isCaregiverSessionType(sessionType))
  }
  return true
}

/**
 * Filters a set of episodes to the active DBT for Teens episodes.
 *
 * Used for the parent's session view. Per the confirmed backend flow (LCDELIVERY-7302), a parent
 * discovers their teen via the guardian link (GET /v1/user/{parentId}/child) and the teen's episodes
 * via GET /v1/patient/{parentId}/child/{teenId}/episodes; this narrows those to the in-progress DBT
 * Teens episode(s) to display. The teen is the episode's `patient_id`.
 *
 * Discovery is guardian-link-based; management of these sessions is separately gated by the backend
 * on the parent being an active SECONDARY_PATIENT participant (behind isDBTTeensEnabled). See the
 * Option E design (LCDELIVERY-7280) — the two links serve different layers.
 */
export const getActiveDbtTeensEpisodes = (episodes?: Episode[]): Episode[] =>
  episodes?.filter(
    (episode) =>
      episode.program_name === ProgramNames.DialecticalBehaviorTherapyTeens &&
      episode.state === EpisodeStates.IN_PROGRESS,
  ) ?? []

/**
 * Returns the lyraId a manage request (reschedule / cancel / ICS) should be keyed on when the
 * logged-in member is acting on someone else's session, or undefined for the default behavior
 * (the logged-in user's own path). The episode patient id is authoritative; childLyraId is the
 * provider-response fallback when a dependent episode has not been cached yet.
 *
 * Deliberately scoped to DBT for Teens (LCDELIVERY-7302): the backend only authorizes
 * parent-JWT-on-teen-path requests for active SECONDARY_PATIENT participants behind
 * isDBTTeensEnabled. Other guardian-visible appointments (e.g. parent-led LCT Teens bookings,
 * which also carry the child's lyraId in `userId`) must keep using the logged-in user's path —
 * re-keying those would 403.
 */
export const getDbtTeensManageTargetLyraId = ({
  appointment,
  currentUserLyraId,
  isDBTTeensEnabled,
  patientLyraId,
}: {
  appointment?: Pick<Appointment, 'userId' | 'programId' | 'childLyraId'> | null
  currentUserLyraId?: string | null
  isDBTTeensEnabled?: boolean
  /** The episode's patient_id. This is authoritative when a caregiver session is owned by the parent. */
  patientLyraId?: string | null
}): string | undefined => {
  if (!isDBTTeensEnabled || appointment == null) {
    return undefined
  }
  if (getProgramNameFromId(appointment.programId) !== ProgramNames.DialecticalBehaviorTherapyTeens) {
    return undefined
  }
  const target = patientLyraId ?? appointment.childLyraId ?? appointment.userId
  return target && currentUserLyraId != null && target !== currentUserLyraId ? target : undefined
}

/**
 * Applies the DBT for Teens visibility rules to a member's appointment list (LCDELIVERY-7302):
 *  - MVP: DBT Teens group sessions are excluded from the member-facing session list.
 *  - Teens view all session types except the caregiver ("Parenting") session, which is hidden from
 *    a teen's list. The viewer's role is resolved per appointment from the appointment's episode —
 *    the episode's patient_id (authoritatively the teen) and its participants (the caregiver is a
 *    SECONDARY_PATIENT) — falling back to the appointment's own fields when the episode isn't loaded.
 *
 * Shared by all web and mobile appointment surfaces so the rules cannot drift between platforms.
 * Callers gate on the isDBTTeensEnabled flag.
 */
export const filterDbtTeensVisibleAppointments = <T extends Appointment>(
  appointments: T[],
  { currentUserLyraId, episodes }: { currentUserLyraId?: string | null; episodes?: Episode[] },
): T[] =>
  appointments.filter((appointment) => {
    const episode = episodes?.find((ep) => ep?.id === appointment.episodeId)
    const programName = episode?.program_name ?? getProgramNameFromId(appointment.programId)
    if (programName !== ProgramNames.DialecticalBehaviorTherapyTeens) {
      return true
    }
    if (isGroupSessionType(appointment.sessionType)) {
      return false
    }
    if (isCaregiverSessionType(appointment.sessionType)) {
      const role = getDbtTeensParticipantRole({
        currentUserLyraId,
        patientId: episode?.patient_id ?? appointment.childLyraId ?? appointment.userId,
        episodeParticipants: episode?.participants,
        appointments: [appointment],
      })
      return role === RecipientType.CAREGIVER
    }
    return true
  })

/**
 * Applies the member-facing DBT Teens visibility rules without losing provider grouping. Use this
 * for surfaces that select providers and derive their own appointment cards or reminders.
 */
export const filterDbtTeensVisibleProviders = (
  providers: Provider[],
  options: { currentUserLyraId?: string | null; episodes?: Episode[] },
): Provider[] =>
  providers.map((provider) => ({
    ...provider,
    appointments: filterDbtTeensVisibleAppointments(provider.appointments, options),
  }))

const DA_FAMILY_CLIENT_INTAKE = 'daFamilyClientIntake'
const DBT_TEENS_GUARDIAN_INTAKE = 'dbttParentIntake'
const CAREGIVER_CONTACT_INTAKE_FORMS = [DA_FAMILY_CLIENT_INTAKE, DBT_TEENS_GUARDIAN_INTAKE]

export type IntakeCaregiverContact = {
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
  canLeaveVoicemail?: boolean
}

/**
 * Extracts caregiver contact info (name, phone, email, voicemail consent) from a completed
 * caregiver/parent intake form for the episode (DA Family or DBT Teens guardian intake).
 *
 * Supports both intake field-naming conventions:
 *  - name: parentGuardianFirstName / parentGuardianLastName
 *  - phone: primaryPhoneNumber > parentGuardianPhone > parentGuardianCellPhone1
 *  - email: parentGuardianEmail > parentGuardianEmail1
 *  - voicemail: canLeaveVoicemail > parentGuardianCanLeaveVoicemail
 *
 * Returns an empty object if no completed intake is found for the episode.
 */
export const getCaregiverContactFromIntakeForm = (
  assignments: Assignment[] | undefined,
  episodeId: string | null | undefined,
): IntakeCaregiverContact => {
  if (!assignments || !episodeId) {
    return {}
  }

  const intakeAssignment = assignments.find(
    (a) =>
      CAREGIVER_CONTACT_INTAKE_FORMS.includes(a.content.name) && a.episode_id === episodeId && a.status === 'completed',
  )

  if (!intakeAssignment) {
    return {}
  }

  const completedResponse = intakeAssignment.assignment_responses?.find((r) => r.status === 'completed')
  if (!completedResponse) {
    return {}
  }

  const response = completedResponse.response
  const firstName = response?.parentGuardianFirstName || undefined
  const lastName = response?.parentGuardianLastName || undefined
  const phone =
    response?.primaryPhoneNumber || response?.parentGuardianPhone || response?.parentGuardianCellPhone1 || undefined
  const email = response?.parentGuardianEmail || response?.parentGuardianEmail1 || undefined
  const canLeaveVoicemail =
    response?.canLeaveVoicemail === true ||
    response?.canLeaveVoicemail === 'true' ||
    response?.parentGuardianCanLeaveVoicemail === true ||
    response?.parentGuardianCanLeaveVoicemail === 'true'

  return {
    ...(firstName && { firstName }),
    ...(lastName && { lastName }),
    ...(phone && { phone }),
    ...(email && { email }),
    ...(canLeaveVoicemail && { canLeaveVoicemail }),
  }
}

const DA_CLIENT_INTAKES = [
  'daFamilyClientIntake',
  'daIndividualClientIntake',
  'daCouplesClientIntake',
  'daAdolescentClientIntake',
  'daChildClientIntake',
  'bctClientIntake',
]

export type IntakeEmergencyContact = {
  firstName?: string
  lastName?: string
  phone?: string
  relationship?: string
}

const findCompletedIntakeResponse = (
  assignments: Assignment[] | undefined,
  episodeId?: string | null,
): Record<string, any> | undefined => {
  if (!assignments) return undefined

  const intakeAssignment = assignments.find(
    (a) =>
      DA_CLIENT_INTAKES.includes(a.content.name) &&
      (!episodeId || a.episode_id === episodeId) &&
      a.status === 'completed',
  )
  if (!intakeAssignment) return undefined

  const completedResponse = intakeAssignment.assignment_responses?.find((r) => r.status === 'completed')
  return completedResponse?.response
}

/**
 * Extracts emergency contact info from a completed DA or BCT client intake form.
 * Supports multiple intake form field naming conventions.
 */
export const getEmergencyContactFromIntakeForm = (
  assignments: Assignment[] | undefined,
  episodeId?: string | null,
): IntakeEmergencyContact => {
  const response = findCompletedIntakeResponse(assignments, episodeId)
  if (!response) return {}

  const firstName =
    response.emergencyContactFirstName || response.firstName_25 || response.parentGuardianFirstName || undefined
  const lastName = response.emergencyContactLastName || response.name_26 || response.parentGuardianLastName || undefined
  const phone =
    response.emergencyContactPhoneNumber || response.phoneNumber_28 || response.emergencyContactPhone || undefined
  const relationship = response.emergencyContactRelationship || response.relationship_27 || undefined

  return {
    ...(firstName && { firstName }),
    ...(lastName && { lastName }),
    ...(phone && { phone }),
    ...(relationship && { relationship }),
  }
}

export const getSelectedOnsiteAddress = (
  selectedAddressOption: Address['id'] | null | undefined,
  addresses: Address[] | undefined,
): Address | null =>
  selectedAddressOption && addresses ? (addresses.find((addr) => addr.id === selectedAddressOption) ?? null) : null

// locationId is not persisted in the appointment DB table, so it is always null
// when fetching existing appointments. Match the persisted address string (which
// contains the booked address) against the provider's addresses using street1 to
// recover the correct Address object for display.
export const getBookedAddressByStreet = (
  appointmentAddress: string | undefined | null,
  providerAddresses: Address[] | undefined,
): Address | null => {
  if (!appointmentAddress || !providerAddresses?.length) return null
  const segments = appointmentAddress
    .toLowerCase()
    .split(',')
    .map((s) => s.trim())
  return (
    providerAddresses.find((addr) => {
      const street = addr.street1?.trim().toLowerCase()
      return street ? segments.some((seg) => seg === street) : false
    }) ?? null
  )
}

export const getSelectedOnsiteLocationKey = (
  isMultipleOnsiteIPCLocationEnabled: boolean,
  isProviderOnsite: boolean,
  street1: string | null | undefined,
  zipcode: string | null | undefined,
): string | null => {
  if (!isMultipleOnsiteIPCLocationEnabled || !isProviderOnsite || !street1 || !zipcode) return null
  return `${street1.toLowerCase().trim()}_${zipcode.toLowerCase().trim()}`
}

// Gets a stable matching key from an in-person location using street1 + zipcode.
// IDs differ between services and lat/lon is absent in calendar slot locations.
export const getLocationKey = ({ location }: { location?: InPersonLocation }): string | null => {
  const street1 = location?.street1
  const zipcode = location?.zipcode ?? location?.zip
  if (!street1 || !zipcode) return null
  return `${street1.toLowerCase().trim()}_${zipcode.toLowerCase().trim()}`
}

// Onsite Flow: filter and transform a single time slot against the pre-selected onsite location.
// Slots at the matching location are kept as-is.
// VIDEO slots are always kept (unaffected by onsite filtering).
// BOTH slots at a non-matching location are downgraded to VIDEO.
// IN_PERSON slots at a non-matching location are excluded.
// Slots without a modality_type (non-IPC providers) are kept as-is.
export const filterTimeSlotByOnsiteLocation = <
  T extends { modality_type?: ModalityType; location?: InPersonLocation },
>({
  timeSlot,
  selectedOnsiteLocationKey,
}: {
  timeSlot: T
  selectedOnsiteLocationKey: string
}): T[] => {
  const locationMatches = getLocationKey({ location: timeSlot.location }) === selectedOnsiteLocationKey
  // If the address/location matches, return the slot as-is.
  if (locationMatches) return [timeSlot]
  // If the address/location doesn't match, but the slot is a video modality slot, return the slot as-is.
  if (timeSlot.modality_type === ModalityType.VIDEO) return [timeSlot]
  // If the address/location doesn't match, but the slot is a both modality slot, downgrade it to video and return it.
  if (timeSlot.modality_type === ModalityType.BOTH_IN_PERSON_AND_VIDEO) {
    return [{ ...timeSlot, modality_type: ModalityType.VIDEO, location: undefined }]
  }
  // If the address/location doesn't match, but the slot is an-person modality slot, drop it completely from the list.
  if (timeSlot.modality_type === ModalityType.IN_PERSON) return []
  // If the address/location don't match and modality does not exist, just return it as is
  // This means that the provider isn't on IPC and needs to just simply show their timeslots as is.
  return [timeSlot]
}

// Filter a provider calendar entry against the selected onsite location.
// When timeSlotsWithModality is absent the provider is non-IPC, so the entry passes through unchanged
// (matching the slot-level fallback in filterTimeSlotByOnsiteLocation).
// Returns null only when filtering left zero slots.
export const filterEntryByOnsiteLocation = (
  entry: ProviderCalendarEntry,
  selectedOnsiteLocationKey: string,
): ProviderCalendarEntry | null => {
  if (!entry.timeSlotsWithModality) return entry
  const filteredSlots = entry.timeSlotsWithModality.flatMap((slot) =>
    filterTimeSlotByOnsiteLocation({ timeSlot: slot, selectedOnsiteLocationKey }),
  )
  if (filteredSlots.length === 0) return null
  return {
    ...entry,
    timeSlotsWithModality: filteredSlots,
    times: filteredSlots.map((slot) => slot.time),
  }
}

export const filterEntriesByOnsiteLocation = (
  entries: ProviderCalendarEntry[] | undefined,
  selectedOnsiteLocationKey: string,
): ProviderCalendarEntry[] | undefined => {
  if (!entries) return entries
  return entries.flatMap((entry) => {
    const filtered = filterEntryByOnsiteLocation(entry, selectedOnsiteLocationKey)
    return filtered ? [filtered] : []
  })
}

export const filterBreakdownByOnsiteLocation = (
  breakdown: ProviderAvailabilityBreakdown | undefined,
  selectedOnsiteLocationKey: string,
): ProviderAvailabilityBreakdown | undefined => {
  if (!breakdown) return breakdown
  return {
    mornings: filterEntriesByOnsiteLocation(breakdown.mornings, selectedOnsiteLocationKey) ?? [],
    afternoons: filterEntriesByOnsiteLocation(breakdown.afternoons, selectedOnsiteLocationKey) ?? [],
    evenings: filterEntriesByOnsiteLocation(breakdown.evenings, selectedOnsiteLocationKey) ?? [],
    weekends: filterEntriesByOnsiteLocation(breakdown.weekends, selectedOnsiteLocationKey) ?? [],
  }
}

// For the provider profile fast-track button, constrain the Arbiter-precomputed
// matchingScheduleSlotsString and the Calendar Service's providerFirstAvailability (both
// location-blind) to slots that survive filterTimeSlotByOnsiteLocation against the selected
// onsite location (location-match, VIDEO, BOTH-downgraded to VIDEO, or non-IPC pass-through).
// When selectedOnsiteLocationKey is null or there is no calendar to validate against, the inputs
// pass through unchanged.
export const filterMatchingAvailabilityByOnsiteLocation = ({
  selectedOnsiteLocationKey,
  providerCalendar,
  fallbackTimeZone,
  rawMatchingScheduleSlotsString,
  providerFirstAvailability,
}: {
  selectedOnsiteLocationKey: string | null
  providerCalendar: ProviderCalendar | undefined
  fallbackTimeZone: string
  rawMatchingScheduleSlotsString: string[]
  providerFirstAvailability: Date | undefined
}): {
  matchingScheduleSlotsString: string[]
  filteredProviderFirstAvailability: Date | undefined
} => {
  if (selectedOnsiteLocationKey == null || providerCalendar == null) {
    return {
      matchingScheduleSlotsString: rawMatchingScheduleSlotsString,
      filteredProviderFirstAvailability: providerFirstAvailability,
    }
  }
  const providerTimeZone = providerCalendar.timeZoneId || fallbackTimeZone
  const validUTCTimes = new Set<number>()
  let earliestMatch: Date | undefined
  const recordTime = (date: string, time: string) => {
    const utc = zonedTimeToUtc(`${date}T${time}`, providerTimeZone)
    validUTCTimes.add(utc.getTime())
    if (!earliestMatch || utc < earliestMatch) {
      earliestMatch = utc
    }
  }
  ;(providerCalendar.availability ?? []).forEach((entry) => {
    // Non-IPC entries lack timeSlotsWithModality; pass their times through unchanged to mirror
    // filterEntryByOnsiteLocation's contract (and the slot-level fallback in
    // filterTimeSlotByOnsiteLocation).
    if (!entry.timeSlotsWithModality) {
      entry.times?.forEach((time) => recordTime(entry.date, time))
      return
    }
    entry.timeSlotsWithModality.forEach((slot) => {
      const filtered = filterTimeSlotByOnsiteLocation({ timeSlot: slot, selectedOnsiteLocationKey })
      if (filtered.length === 0) return
      recordTime(entry.date, slot.time)
    })
  })
  return {
    matchingScheduleSlotsString: rawMatchingScheduleSlotsString.filter((slotString) =>
      validUTCTimes.has(new Date(slotString).getTime()),
    ),
    filteredProviderFirstAvailability: earliestMatch,
  }
}

export const isSessionOlderThan90Days = (
  appointment: Pick<Appointment, 'startDate' | 'startTime' | 'timeZone'> | null | undefined,
): boolean => {
  if (!appointment?.startDate || !appointment?.startTime) {
    return false
  }
  const appointmentStartUTC = getDateFromAppointment(appointment)
  return appointmentStartUTC < subDays(new Date(), 90)
}
